import { AlertTriangle, CalendarDays, ClipboardList, Flag, FolderPlus, MessagesSquare, Server } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/common/States'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { fmtDate } from '../../shared/format'
import type { ProjectSummary } from '../types'
import { useStaffLookup } from '../ui'
import { MemberStack, ProgressBar, ProjectIcon, ProjectKindBadge, ProjectStatusBadge, ScheduleBadge } from './ProjectBits'

type Props = {
  projects: ProjectSummary[]
  loading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  emptyAction?: ReactNode
}

function Stat({ icon: Icon, value, label }: { icon?: typeof ClipboardList; value: ReactNode; label: string }) {
  return (
    <div className="min-w-0 rounded-lg bg-muted/50 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold tabular-nums">
        {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden /> : null}
        <span className="truncate">{value}</span>
      </div>
      <div className="truncate text-[11px] text-muted-foreground">{label}</div>
    </div>
  )
}

function ProjectCard({ project: p, selected, onSelect }: { project: ProjectSummary; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        'group flex h-full w-full flex-col gap-4 rounded-xl border bg-card p-4 text-left shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-card-hover motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-5',
        selected && 'border-primary/60 bg-primary/[0.04] shadow-card-hover ring-2 ring-primary/25 dark:bg-primary/10',
      )}
    >
      <div className="flex items-start gap-3">
        <ProjectIcon icon={p.icon} color={p.color} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold leading-tight">{p.name}</h3>
            {p.status !== 'active' ? <ProjectStatusBadge status={p.status} /> : null}
            {p.kind === 'custom' ? <ProjectKindBadge kind={p.kind} /> : null}
            <ScheduleBadge endDate={p.endDate} status={p.status} />
          </div>
          <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{p.description}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Stat icon={ClipboardList} value={p.activeTasks} label={t('projects.stats.activeTasks')} />
        <Stat icon={MessagesSquare} value={p.pendingApproval} label={t('projects.stats.pendingApproval')} />
        <Stat icon={Server} value={p.activeInstallations} label={t('projects.stats.installations')} />
      </div>
      <ProgressBar value={p.progress} label={t('projects.stats.progress')} />
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="size-3.5" aria-hidden />
          {t('projects.fields.startShort', { date: fmtDate(p.startDate) })}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Flag className="size-3.5" aria-hidden />
          {t('projects.fields.endShort', { date: p.endDate ? fmtDate(p.endDate) : t('projects.fields.noEnd') })}
        </span>
        {p.overdueTasks > 0 ? (
          <span className="inline-flex items-center gap-1.5 font-medium text-destructive">
            <AlertTriangle className="size-3.5" aria-hidden />
            {p.overdueTasks} {t('projects.schedule.overdueTasks')}
          </span>
        ) : null}
      </div>
      <div className="mt-auto">
        <MemberStack names={p.memberIds.map(nameOf)} />
      </div>
    </button>
  )
}

export function ProjectCards({ projects, loading, selectedId, onSelect, emptyAction }: Props) {
  const { t } = useTranslation()

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-56 rounded-xl" />
        ))}
      </div>
    )
  }

  if (projects.length === 0) {
    return (
      <div className="rounded-xl border bg-card shadow-card">
        <EmptyState icon={FolderPlus} title={t('projects.empty')} description={t('projects.emptyHint')} action={emptyAction} />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      {projects.map((p) => (
        <ProjectCard key={p.id} project={p} selected={p.id === selectedId} onSelect={() => onSelect(p.id)} />
      ))}
    </div>
  )
}
